import { EpocusRole } from "elephant-web/gql";
import { type EpocusWorkMode, useEpocusWorkMode } from "./epocus-work-mode";
import { useEpocusRole } from "./rbac/epocus-claim";

export type EpocusRoleVariant = "performer" | "reviewer";

/*
 * Deliberately NOT a capability. `viewOwnPerformance` already grants every
 * ePOCUS role access to this route, so there is nothing to authorise here —
 * this only picks which stats are worth showing. Adding it to
 * `_lib/rbac/capabilities.ts` would put a presentation concern in a table that
 * mirrors backend grants 1:1 (see PNE-562).
 *
 * QA Admin and Program Admin resolve to "reviewer" because they hold
 * `submitReview` but not `createEncounter` — the performer variant renders
 * empty for them.
 */
const REVIEWER_VARIANT_ROLES = new Set<EpocusRole>([
  EpocusRole.Reviewer,
  EpocusRole.QaAdmin,
  EpocusRole.ProgramAdmin,
]);

export const getRoleVariant = (role: EpocusRole | null): EpocusRoleVariant =>
  role !== null && REVIEWER_VARIANT_ROLES.has(role) ? "reviewer" : "performer";

/**
 * Like `getRoleVariant` above, deliberately NOT a capability: this picks which
 * nav board to render, and every item on either board is still gated on the
 * capability table.
 */
export const isAdminNavRole = (role: EpocusRole | null): boolean =>
  role === EpocusRole.QaAdmin || role === EpocusRole.ProgramAdmin;

export const hasWorkModeSwitch = (role: EpocusRole | null): boolean =>
  role === EpocusRole.PerformerReviewer;

export const displayRoleFor = (
  role: EpocusRole | null,
  mode: EpocusWorkMode,
): EpocusRole | null => {
  if (!hasWorkModeSwitch(role)) return role;
  return mode === "qa" ? EpocusRole.Reviewer : EpocusRole.Performer;
};

export const useEpocusDisplayRole = (): EpocusRole | null =>
  displayRoleFor(useEpocusRole(), useEpocusWorkMode());

export const useRoleVariant = (): EpocusRoleVariant =>
  getRoleVariant(useEpocusDisplayRole());
